
import { AccountController } from "@/app/ui/controllers/AccountController";
import { Vue } from "vue-class-component";
import generateLabel from "@/app/ui/views/cargo-v2/modules/label-pdf";
import generateLabelPlane from "@/app/ui/views/cargo-v2/modules/label-plane-pdf";
import generateLabelPlaneV2 from "@/app/ui/views/cargo-v2/modules/label-plane-v2-pdf";
import generateLabelPlaneNgen from "@/app/ui/views/cargo-v2/modules/label-plane-ngen";
import generateSPTJElektronikPDF from "@/app/ui/views/cargo-v2/modules/sptj-elektronik-pdf";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import JsBarcode from "jsbarcode";
import { convertQRCode } from "@/app/ui/modules/receipt/module";
import { formatByCountry, parseByCountry } from "@/app/infrastructures/misc/Utils";
import {
  ColumnManifestData,
  HeaderManifestData,
  SectionManifestData
} from "@/domain/entities/MainApp";
import { generateManifestCargoV2 } from "@/app/ui/views/cargo-v2/modules/manifest-pdf-cargo-v2";
import { FlagsUtilsCargo } from "@/feature-flags/flags-utils-cargo";
import { generatePti } from "@/app/ui/views/cargo-v2/modules/generate-pti";
import { generateSptjNonElectronic } from "@/app/ui/views/cargo-v2/modules/generate-sptj-non-electronic";
import { startCase } from "lodash";
import {
  CargoV2DetailActivity,
  CargoV2DetailActivityEnum
} from "@/data/payload/api/CargoV2ApiRequest";
import { CargoV2Data } from "@/domain/entities/CargoV2";
import formatDateBasedTimezoneValue from '@/app/infrastructures/misc/common-library/v2/FormatDateBasedTimezoneValue';

type PrintSuccessFlag = "manifest" | "label" | "pti" | "sptj";

type element = {
  getElementById(id: string): { src: string };
};

export default class Print extends Vue {
  get dataProfile() {
    return AccountController.accountData;
  }

  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  getDetailCargoV2(
    id: string | number,
    activity?: CargoV2DetailActivity
  ): Promise<CargoV2Data> {
    return CargoV2Controller.getDetailCargoV2ForPrint({
      id,
      activity: this.dataProfile.isInternalAccount
        ? CargoV2DetailActivityEnum.IS_INTERNAL
        : activity,
      isPrint: true
    });
  }

  async companyLogoPlaneLabel(prefix: string, short = false) {
    const logoLion = await import("@/app/ui/assets/images/logo-lion-air.png");
    const logoBatik = await import("@/app/ui/assets/images/logo-batik-air.png");
    const logoSuperjet = await import(
      "@/app/ui/assets/images/logo-super-jet-air.png"
    );
    const logoSuperAirJetShort = await import(
      "@/app/ui/assets/images/logo-super-air-jet-short.png"
    );
    const logoWings = await import("@/app/ui/assets/images/logo-wings-air.png");
    const logoLionParcel = await import(
      "@/app/ui/assets/images/logo-lion-parcel.png"
    );

    if (prefix.startsWith("JT")) return logoLion.default;
    if (prefix.startsWith("ID")) return logoBatik.default;
    if (prefix.startsWith("IU"))
      return short ? logoSuperAirJetShort.default : logoSuperjet.default;
    if (prefix.startsWith("IW")) return logoWings.default;
    if (prefix.startsWith("LP")) return logoLionParcel.default;
    return "";
  }

  async companyPlane(
    prefix: string,
    short = false
  ): Promise<{ logo: string; label: string }> {
    const logo = await this.companyLogoPlaneLabel(prefix, short);
    if (prefix.startsWith("JT")) return { logo, label: "LION AIR" };
    if (prefix.startsWith("ID")) return { logo, label: "BATIK AIR" };
    if (prefix.startsWith("IU")) return { logo, label: "SUPER AIR JET" };
    if (prefix.startsWith("IW")) return { logo, label: "WINGS AIR" };
    if (prefix.startsWith("LP")) return { logo, label: "LION PARCEL" };
    return { logo: "", label: "" };
  }

  private parseNumber(value: string | number): number {
    if (typeof value === "number") return value;
    return value ? parseByCountry(String(value), { country: this.countryCode }) : 0;
  }

  async printData(
    id: string | number,
    type: string,
    _name: string,
    _row?: CargoV2Data | null,
    onSuccess?: (flag: PrintSuccessFlag) => void
  ): Promise<void> {
    const cargo: CargoV2Data = await this.getDetailCargoV2(
      id,
      CargoV2DetailActivityEnum.IS_PRINT_LABEL
    );
    cargo.totalActualCargoGrossWeight = this.formatValueNumber(
      cargo.totalActualCargoGrossWeight
    );
    cargo.totalActualCargoVolumeWeight = this.formatValueNumber(
      cargo.totalActualCargoVolumeWeight
    );
    if (!cargo.cargoNumber) return;

    JsBarcode("#barcode", cargo.cargoNumber, {
      textAlign: "left",
      displayValue: false,
      fontSize: 16
    });

    const combineQRText = `${cargo.cargoNumber}#${cargo.vehiclePoliceNumber}#${
      cargo.originCityCode
    } - ${cargo.originCityName}#${cargo.destinationCityCode} - ${
      cargo.destinationCityName
    }#${cargo.airportDestinationCityCode} - ${
      cargo.airportDestinationCityName
    }#${cargo.cargoType}#${cargo.partnerName}#${cargo.cargoCommodity}#${
      cargo.cargoProduct
    }#${cargo.totalStt}#${cargo.cargoActualPiece}#${
      cargo.isUseNewCalculation
        ? cargo.totalActualCargoGrossWeight
        : cargo.totalGrossWeight
    }#${
      cargo.isUseNewCalculation
        ? cargo.totalActualCargoVolumeWeight
        : cargo.totalVolumeWeight
    }`;
    const QRCode = await convertQRCode(combineQRText);

    const element = (document as unknown) as element;
    const companyLogo = element.getElementById("company-logo").src;
    const barcode = element.getElementById("barcode").src;
    const companyLogoPlaneLabel = await this.companyLogoPlaneLabel(
      cargo.vehiclePoliceNumber
    );

    if (type === "label" && cargo.cargoType !== "plane") {
      await generateLabel(cargo, companyLogo, barcode, QRCode);
    } else {
      await this.labelPlane(cargo, companyLogoPlaneLabel, barcode);
    }
    onSuccess?.("label");
  }

  async labelPlane(
    cargo: CargoV2Data,
    companyLogoPlaneLabel: string,
    barcode: string
  ): Promise<void> {
    if (cargo.isNewForm) {
      await generateLabelPlaneV2(cargo, companyLogoPlaneLabel);
    } else {
      await generateLabelPlaneNgen(cargo, companyLogoPlaneLabel);
    }
  }

  formatValueNumber(number: unknown): string {
    const val = typeof number === "string"
      ? parseByCountry(number, { country: this.countryCode })
      : Number(number);
    return formatByCountry(val, { type: "weight", country: this.countryCode });
  }

  printManifestV2 = async (
    id: string | number,
    _row?: CargoV2Data | null,
    onSuccess?: (flag: PrintSuccessFlag) => void
  ): Promise<void> => {
    const dataManifest = await this.getDetailCargoV2(
      id,
      CargoV2DetailActivityEnum.IS_PRINT_MANIFEST
    );
    if (!dataManifest) return;

    dataManifest.totalActualCargoGrossWeight = this.formatValueNumber(
      dataManifest.totalActualCargoGrossWeight
    );
    dataManifest.totalActualCargoVolumeWeight = this.formatValueNumber(
      dataManifest.totalActualCargoVolumeWeight
    );
    JsBarcode("#barcode", dataManifest.cargoNumber, {
      textAlign: "left",
      displayValue: false,
      fontSize: 16
    });
    const element = (document as unknown) as {
      getElementById(id: string): { src: string };
    };
    const barcode = element.getElementById("barcode").src;
    const combineQRText = `${dataManifest.cargoNumber}#${
      dataManifest.vehiclePoliceNumber
    }#${dataManifest.originCityCode} - ${dataManifest.originCityName}#${
      dataManifest.destinationCityCode
    } - ${dataManifest.destinationCityName}#${
      dataManifest.airportDestinationCityCode
    } - ${dataManifest.airportDestinationCityName}#${dataManifest.cargoType}#${
      dataManifest.partnerName
    }#${dataManifest.cargoCommodity}#${dataManifest.cargoProduct}#${
      dataManifest.totalStt
    }#${dataManifest.cargoActualPiece}#${
      dataManifest.isUseNewCalculation
        ? dataManifest.totalActualCargoGrossWeight
        : dataManifest.totalGrossWeight
    }#${
      dataManifest.isUseNewCalculation
        ? dataManifest.totalActualCargoVolumeWeight
        : dataManifest.totalVolumeWeight
    }`;
    const QRCode = await convertQRCode(combineQRText);
    const data = new SectionManifestData({
      featureName: `Cargo Manifest [${dataManifest.cargoNumber}]`,
      headerDetailData: new HeaderManifestData({
        noBag: dataManifest.noBag,
        originCity: `${dataManifest.originCityCode} - ${dataManifest.originCityName}`,
        destinationCity: `${dataManifest.destinationCityCode} - ${dataManifest.destinationCityName}`,
        createdName: dataManifest.createdName,
        totalStt: dataManifest.totalStt,
        createdAtPdf: formatDateBasedTimezoneValue(dataManifest.createdAt),
        updatedByName: dataManifest.updatedByName,
        updatedAtPdf: formatDateBasedTimezoneValue(dataManifest.updatedAt),
        cargoCommodity: dataManifest.cargoCommodity,
        cargoProduct: dataManifest.cargoProduct,
        totalPiece: dataManifest.cargoActualPiece,
        totalActualCargoVolumeWeight: dataManifest.totalActualCargoVolumeWeight,
        totalActualCargoGrossWeight: dataManifest.totalActualCargoGrossWeight,
        totalActualCargoChargeableWeight: String(
          Math.max(
            this.parseNumber(dataManifest.totalActualCargoGrossWeight),
            this.parseNumber(dataManifest.totalActualCargoVolumeWeight)
          )
        ),
        totalGrossWeight: dataManifest.totalGrossWeight,
        totalVolumeWeight: dataManifest.totalVolumeWeight,
        sttTotalPiece: dataManifest.sttTotalPiece,
        updatedAt: dataManifest.updatedAt,
        updatedBy: dataManifest.updatedBy,
        cargoType: dataManifest.cargoType
      }),
      headerSection: [
        "noBag",
        "originCity",
        "destinationCity",
        "createdName",
        "createdAtPdf",
        "updatedByName",
        "updatedAtPdf",
        "partnerName",
        "departureDate",
        "arrivalDate",
        "cargoCommodity",
        "cargoProduct",
        "totalStt",
        "totalPiece",
        "totalGrossWeight",
        "totalVolumeWeight",
        "sttTotalPiece",
        "sttVolumeWeight",
        "sttGrossWeight",
        "totalActualCargoChargeableWeight"
      ],
      data: dataManifest.detailStt.map(
        (stt: {
          baggingVendorNumber?: string;
          baggingNumber?: string;
          sttNumber: string;
          grossWeigth: number;
          destination: string;
          sttTotalPiece: number;
          sttGrossWeight: number;
          sttVolumeWeight: number;
          sttProductType: string;
          sttCommodityName: string;
          sttOriginCityId: number;
          sttDestinationCityId: number;
        }) =>
          new ColumnManifestData({
            bagNo: stt.baggingVendorNumber || stt.baggingNumber || "-",
            sttNo: stt.sttNumber,
            totalGrossWeight: stt.grossWeigth,
            destinationCity: stt.destination,
            sttTotalPiece: stt.sttTotalPiece,
            sttGrossWeight: stt.sttGrossWeight,
            sttVolumeWeight: stt.sttVolumeWeight,
            sttProductType: stt.sttProductType,
            sttCommodityName: stt.sttCommodityName,
            sttOriginCityId: stt.sttOriginCityId,
            sttDestinationCityId: stt.sttDestinationCityId
          })
      ),
      totalColumnHeader: 2,
      signature: true
    });
    generateManifestCargoV2(data, barcode, QRCode);
    onSuccess?.("manifest");
  };

  async printSPTJElektronik(
    cargoNumber: string | number,
    _row?: CargoV2Data | null,
    onSuccess?: (flag: PrintSuccessFlag) => void
  ): Promise<void> {
    const cargo = await this.getDetailCargoV2(
      cargoNumber,
      CargoV2DetailActivityEnum.IS_PRINT_SPTJ
    );
    if (!cargo) return;

    const date = new Date();
    const months = [
      "Januari",
      "Februari",
      "Maret",
      "April",
      "Mei",
      "Juni",
      "Juli",
      "Agustus",
      "September",
      "Oktober",
      "November",
      "Desember"
    ];
    const formattedDate = `${date.getDate()} ${
      months[date.getMonth()]
    } ${date.getFullYear()}`;
    const data = {
      name: this.dataProfile.name || "",
      position: "",
      companyName: this.dataProfile.account_type_detail?.company_name || "",
      smuNo: cargo.cargoNumber,
      flightNo: cargo.vehiclePoliceNumber,
      route: cargo.destinationCityCode,
      electronicGoods: cargo.nog,
      date: `${startCase(cargo.originCityName.toLowerCase())}, ${formattedDate}`
    };
    await generateSPTJElektronikPDF(data);
    onSuccess?.("sptj");
  }

  printPtiPdf = async (
    id: string | number,
    _row?: CargoV2Data | null,
    onSuccess?: (flag: PrintSuccessFlag) => void
  ): Promise<void> => {
    const data = await this.getDetailCargoV2(
      id,
      CargoV2DetailActivityEnum.IS_PRINT_PTI
    );
    if (!data) return;

    data.totalActualCargoGrossWeight = this.formatValueNumber(
      data.totalActualCargoGrossWeight
    );
    const companyPlane = await this.companyPlane(data.vehiclePoliceNumber);
    await generatePti({
      companyPlane,
      data,
      accountData: AccountController.accountData
    });
    onSuccess?.("pti");
  };

  printSptjNonElectronic = async (
    id: string | number,
    _row?: CargoV2Data | null,
    onSuccess?: (flag: PrintSuccessFlag) => void
  ): Promise<void> => {
    const data = await this.getDetailCargoV2(
      id,
      CargoV2DetailActivityEnum.IS_PRINT_SPTJ
    );
    if (!data) return;

    data.totalActualCargoGrossWeight = this.formatValueNumber(
      data.totalActualCargoGrossWeight
    );
    const companyPlanes = await Promise.all([
      this.companyPlane("JT"),
      this.companyPlane("ID"),
      this.companyPlane("IU", true),
      this.companyPlane("IW"),
      this.companyPlane("LP")
    ]);
    await generateSptjNonElectronic({
      companyPlanes,
      data
    });
    onSuccess?.("sptj");
  };

  get roleAccount(): string {
    return (
      (this.dataProfile.account_type === "partner"
        ? this.dataProfile.account_type_detail?.type
        : this.dataProfile.account_type) || "internal"
    );
  }
}
